import React from 'react';
import { useTranslation } from 'react-i18next';

interface HeroSectionProps {
  onConsultClick: () => void;
}

const HeroSection = ({ onConsultClick }: HeroSectionProps) => {
  const { t } = useTranslation('latvia');

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
      <div
        className="absolute inset-0 bg-cover bg-center"
        style={{
          backgroundImage:
            'url(https://public.readdy.ai/ai/img_res/6c6f1f71a9a14a4fd2ff0430e6194e97.jpg)',
        }}
      ></div>
      <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-black/60"></div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center w-full pt-16 pb-6 md:pt-0 md:pb-0">
        {/* Company Logo */}
        <div className="flex justify-center mb-4 md:mb-8">
          <img
            src="https://public.readdy.ai/ai/img_res/8ceb91fe-4019-4031-9c6b-5dbc6d2105e5.png"
            alt="The Daeg International Logo"
            className="h-16 sm:h-24 md:h-36 w-auto object-contain"
            style={{
              filter:
                'drop-shadow(0 0 8px rgba(255,255,255,0.95)) drop-shadow(0 0 20px rgba(255,255,255,0.8)) drop-shadow(0 0 40px rgba(255,255,255,0.6)) drop-shadow(0 0 60px rgba(255,255,255,0.4))',
            }}
          />
        </div>

        <h1 className="text-2xl sm:text-4xl md:text-7xl font-bold mb-2.5 md:mb-6 leading-snug md:leading-tight font-serif">
          <span
            className="text-black"
            style={{
              textShadow: '0 0 12px rgba(255,255,255,0.9), 0 0 24px rgba(255,255,255,0.7), 0 0 40px rgba(255,255,255,0.5), 2px 2px 6px rgba(255,255,255,0.8)',
            }}
          >
            {t('hero.title.invest')}
          </span>
          <br />
          <span className="text-gold-400">{t('hero.title.card')}</span>
        </h1>
        <p className="text-xs sm:text-lg md:text-2xl text-gray-300 mb-4 md:mb-8 max-w-3xl mx-auto leading-relaxed">
          {t('hero.description')}
        </p>
        <div className="flex flex-col sm:flex-row gap-2.5 md:gap-4 justify-center items-center">
          <button
            onClick={onConsultClick}
            className="w-full sm:w-auto px-5 md:px-8 py-2.5 md:py-4 bg-gold-600 text-white text-xs md:text-lg font-semibold rounded-lg hover:bg-gold-700 transition-all transform hover:scale-105 shadow-lg whitespace-nowrap cursor-pointer"
          >
            {t('hero.cta.consult')}
          </button>
          <a
            href="#benefits"
            className="w-full sm:w-auto text-center px-5 md:px-8 py-2.5 md:py-4 bg-white/10 backdrop-blur-sm text-white text-xs md:text-lg font-semibold rounded-lg hover:bg-white/20 transition-all border-2 border-white/30 whitespace-nowrap cursor-pointer"
          >
            {t('hero.cta.learnMore')}
          </a>
        </div>

        <div className="grid grid-cols-3 gap-2 md:gap-6 mt-6 md:mt-16 max-w-4xl mx-auto">
          <div className="bg-white/10 backdrop-blur-md rounded-lg md:rounded-xl p-2.5 md:p-6 border border-white/20">
            <div className="w-7 h-7 md:w-12 md:h-12 flex items-center justify-center bg-gold-500/20 rounded-full mb-1.5 md:mb-4 mx-auto">
              <i className="ri-time-line text-sm md:text-2xl text-gold-400"></i>
            </div>
            <h3 className="text-sm sm:text-lg md:text-2xl font-bold text-white mb-0.5 md:mb-2">
              {t('hero.stat1.title')}
            </h3>
            <p className="text-gray-200 text-[9px] sm:text-xs md:text-sm leading-snug">
              {t('hero.stat1.desc')}
            </p>
          </div>

          <div className="bg-white/10 backdrop-blur-md rounded-lg md:rounded-xl p-2.5 md:p-6 border border-white/20">
            <div className="w-7 h-7 md:w-12 md:h-12 flex items-center justify-center bg-gold-500/20 rounded-full mb-1.5 md:mb-4 mx-auto">
              <i className="ri-team-line text-sm md:text-2xl text-gold-400"></i>
            </div>
            <h3 className="text-sm sm:text-lg md:text-2xl font-bold text-white mb-0.5 md:mb-2">
              {t('hero.stat2.title')}
            </h3>
            <p className="text-gray-200 text-[9px] sm:text-xs md:text-sm leading-snug">
              {t('hero.stat2.desc')}
            </p>
          </div>

          <div className="bg-white/10 backdrop-blur-md rounded-lg md:rounded-xl p-2.5 md:p-6 border border-white/20">
            <div className="w-7 h-7 md:w-12 md:h-12 flex items-center justify-center bg-gold-500/20 rounded-full mb-1.5 md:mb-4 mx-auto">
              <i className="ri-map-pin-line text-sm md:text-2xl text-gold-400"></i>
            </div>
            <h3 className="text-sm sm:text-lg md:text-2xl font-bold text-white mb-0.5 md:mb-2">
              {t('hero.stat3.title')}
            </h3>
            <p className="text-gray-200 text-[9px] sm:text-xs md:text-sm leading-snug">
              {t('hero.stat3.desc')}
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;